/**
 * AppICEMAP — InstallPrompt
 * Custom element: `<install-prompt>`
 *
 * Bottom toast/sheet prompting users to install the PWA.
 * Uses the `beforeinstallprompt` event. Shows only if:
 *   - Not already installed
 *   - User hasn't previously dismissed
 *   - The browser fires beforeinstallprompt
 *
 * Events: none (self-contained)
 */

const DISMISSED_KEY = "appicemap-install-dismissed";

export class InstallPrompt extends HTMLElement {
	constructor() {
		super();
		this._deferredPrompt = null;
		this._dismissed = false;
	}

	connectedCallback() {
		// Check if previously dismissed
		try {
			this._dismissed = localStorage.getItem(DISMISSED_KEY) === "1";
		} catch {
			this._dismissed = false;
		}

		// Check if already standalone
		if (this._isStandalone()) return;

		// Listen for install prompt
		window.addEventListener("beforeinstallprompt", (e) => {
			e.preventDefault();
			this._deferredPrompt = e;

			if (!this._dismissed) {
				this._show();
			}
		});

		// Check if already installed after the fact
		window.addEventListener("appinstalled", () => {
			this._deferredPrompt = null;
			this._hide();
		});
	}

	/* ── Private ─────────────────────────────────────────────────────────── */

	_isStandalone() {
		return (
			window.matchMedia("(display-mode: standalone)").matches ||
			window.navigator.standalone === true
		);
	}

	_show() {
		if (this._dismissed) return;
		this.style.display = "block";
		this._render();
		this._bindEvents();
	}

	_hide() {
		this.style.display = "none";
		this.innerHTML = "";
	}

	_render() {
		this.innerHTML = `
			<style>
				:host {
					display: none;
					position: fixed; bottom: 0; left: 0; right: 0; z-index: 950;
					animation: ip-slide-up var(--duration-normal, 250ms) var(--easing-decelerate);
				}
				.ip {
					display: flex; align-items: center; gap: var(--space-3, 12px);
					margin: var(--space-3, 12px);
					padding: var(--space-4, 16px);
					background: var(--color-surface, #232532);
					border-radius: var(--radius-lg, 14px);
					box-shadow: var(--shadow-lg);
					border: 1px solid var(--color-neutral-700, #595d6c);
				}
				.ip__icon {
					font-size: 1.5rem;
					color: var(--color-accent, #9184d9);
					flex-shrink: 0;
				}
				.ip__text {
					flex: 1; min-width: 0;
				}
				.ip__title {
					font-size: var(--text-base, 0.9375rem);
					font-weight: 600;
					color: var(--color-text, #e9e9ed);
				}
				.ip__sub {
					font-size: var(--text-xs, 0.6875rem);
					color: var(--color-neutral-400, #b2b6ca);
					margin-top: 2px;
				}
				.ip__actions {
					display: flex; gap: var(--space-2, 8px); flex-shrink: 0;
				}
				.ip__btn {
					padding: var(--space-2, 8px) var(--space-4, 16px);
					border: none; border-radius: var(--radius-full, 9999px);
					font-size: var(--text-sm, 0.8125rem); font-weight: 500;
					cursor: pointer; white-space: nowrap;
					transition: opacity var(--duration-fast, 150ms);
				}
				.ip__btn--install {
					background: var(--color-accent, #9184d9);
					color: #fff;
				}
				.ip__btn--install:hover { opacity: 0.85; }
				.ip__btn--dismiss {
					background: transparent;
					color: var(--color-neutral-400, #b2b6ca);
				}
				.ip__btn--dismiss:hover { color: var(--color-text, #e9e9ed); }

				@keyframes ip-slide-up {
					from { transform: translateY(100%); opacity: 0; }
					to { transform: translateY(0); opacity: 1; }
				}
			</style>
			<div class="ip">
				<i class="ph ph-download-simple ip__icon"></i>
				<div class="ip__text">
					<div class="ip__title">Instalar AppICEMAP</div>
					<div class="ip__sub">Accede rápido desde tu pantalla de inicio</div>
				</div>
				<div class="ip__actions">
					<button class="ip__btn ip__btn--dismiss" data-action="dismiss">Ahora no</button>
					<button class="ip__btn ip__btn--install" data-action="install">Instalar</button>
				</div>
			</div>
		`;
	}

	_bindEvents() {
		this.querySelector('[data-action="install"]')?.addEventListener(
			"click",
			() => {
				this._install();
			},
		);

		this.querySelector('[data-action="dismiss"]')?.addEventListener(
			"click",
			() => {
				this._dismiss();
			},
		);
	}

	async _install() {
		if (!this._deferredPrompt) return;

		try {
			await this._deferredPrompt.prompt();
			const { outcome } = await this._deferredPrompt.userChoice;
			console.log("[InstallPrompt] User choice:", outcome);
		} catch (err) {
			console.warn("[InstallPrompt] Install error:", err);
		}

		this._deferredPrompt = null;
		this._hide();
	}

	_dismiss() {
		this._dismissed = true;
		try {
			localStorage.setItem(DISMISSED_KEY, "1");
		} catch {
			/* ignore */
		}
		this._hide();
	}
}

if (!customElements.get("install-prompt")) {
	customElements.define("install-prompt", InstallPrompt);
}
